import { useQuery } from "@tanstack/react-query";
import { type FC, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
	TIKTOK_ADS_CTA_OPTIONS,
	TIKTOK_ADS_MAX_AD_TEXT_LENGTH,
	TIKTOK_ADS_OPTIMIZATION_EVENTS,
	type TikTokAdsCTA,
	TikTokAdsObjective,
} from "shared";
import { Api } from "../api.config";
import { useModal } from "../common/modal/Modal";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { ModalSelectMedia } from "../post/ModalSelectMedia";
import { useTikTokCampaignWizard } from "./TikTokCampaignWizardContext";
import { useTikTokAdsIdentities } from "./useTikTokCampaigns";

export const StepTikTokCreative: FC = () => {
	const { draft, updateDraft, nextStep, prevStep } = useTikTokCampaignWizard();
	const { open } = useModal();
	const [error, setError] = useState("");
	const isLeadGen = draft.objective === TikTokAdsObjective.LEAD_GENERATION;
	const isConversions = draft.objective === TikTokAdsObjective.WEB_CONVERSIONS;
	const {
		data: identities,
		isLoading: identitiesLoading,
		error: identitiesError,
	} = useTikTokAdsIdentities();

	const { data: instantForms, isLoading: formsLoading } = useQuery({
		queryKey: ["TIKTOK_ADS", "INSTANT_FORMS"],
		queryFn: () => Api.GetTikTokAdsInstantForms().then((r) => r.data),
		enabled: isLeadGen,
	});

	const { data: pixels, isLoading: pixelsLoading } = useQuery({
		queryKey: ["TIKTOK_ADS", "PIXELS"],
		queryFn: () => Api.GetTikTokAdsPixels().then((r) => r.data),
		enabled: isConversions,
	});

	const { data: mediaFiles } = useQuery({
		queryKey: ["MEDIA_BATCH", draft.mediaFileId],
		queryFn: () =>
			Api.GetMediaBatch([draft.mediaFileId as number]).then((r) => r.data),
		enabled: draft.mediaFileId !== null,
	});

	const selectedMedia = mediaFiles?.[0];

	const handleSelectMedia = () => {
		open(ModalSelectMedia, {
			onChange: (ids: number[]) => {
				updateDraft({ mediaFileId: ids[0] ?? null });
			},
			values: draft.mediaFileId ? [draft.mediaFileId] : [],
			multiple: false,
		});
	};

	const applyUrl = (url: string) => {
		const updates: Partial<typeof draft> = { landingPageUrl: url };
		if (!draft.displayName) {
			try {
				updates.displayName = new URL(url).hostname.replace(/^www\./, "");
			} catch {
				// Leave the display name alone until the URL parses.
			}
		}
		updateDraft(updates);
	};

	const handleNext = () => {
		if (!draft.identityId) {
			setError("Choose the TikTok account the ad will be published as.");
			return;
		}
		if (!draft.mediaFileId) {
			setError("Select a video for the ad.");
			return;
		}
		if (!draft.adText.trim()) {
			setError("Ad text is required.");
			return;
		}
		if (isLeadGen) {
			if (!draft.instantFormPageId?.trim()) {
				setError("Select a TikTok Instant Form for Lead Generation.");
				return;
			}
		} else if (isConversions) {
			if (!draft.landingPageUrl.trim()) {
				setError("Landing page URL is required.");
				return;
			}
			if (!draft.pixelId?.trim()) {
				setError("Select a TikTok Pixel for Conversions.");
				return;
			}
			if (!draft.optimizationEvent?.trim()) {
				setError("Select a Pixel optimization event for Conversions.");
				return;
			}
		} else if (!draft.landingPageUrl.trim()) {
			setError("Landing page URL is required.");
			return;
		}
		setError("");
		nextStep();
	};

	return (
		<Card>
			<Card.Header>Creative</Card.Header>
			<Card.Body>
				<Form.Group className="mb-3">
					<Form.Label>Publish as</Form.Label>
					{identitiesLoading ? (
						<div>
							<Spinner size="sm" /> Loading TikTok accounts…
						</div>
					) : identitiesError ? (
						<p className="text-danger mb-0">
							Could not load TikTok accounts. Check that your ad account has an
							identity configured in TikTok Ads Manager.
						</p>
					) : (
						<Select
							value={draft.identityId}
							onChange={(e) => {
								const identity = identities?.find(
									(item) => item.id === e.target.value,
								);
								updateDraft({
									identityId: e.target.value,
									identityType: identity?.type ?? "CUSTOMIZED_USER",
									identityAuthorizedBcId: identity?.authorizedBcId ?? null,
								});
							}}
						>
							<option value="">Select an account…</option>
							{(identities ?? []).map((identity) => (
								<option key={identity.id} value={identity.id}>
									{identity.displayName}
								</option>
							))}
						</Select>
					)}
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Video</Form.Label>
					<div>
						{selectedMedia ? (
							<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
								<span>{selectedMedia.filename}</span>
								<Button
									variant="secondary"
									size="sm"
									onClick={handleSelectMedia}
								>
									Change
								</Button>
							</div>
						) : (
							<Button variant="secondary" onClick={handleSelectMedia}>
								Select video from media library
							</Button>
						)}
					</div>
					<Form.Text>
						TikTok ads are video-only. Vertical 9:16 performs best.
					</Form.Text>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Ad name</Form.Label>
					<Form.Control
						value={draft.adName}
						onChange={(e) => updateDraft({ adName: e.target.value })}
						placeholder={`${draft.name} — Ad`}
					/>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Ad text</Form.Label>
					<Form.Control
						as="textarea"
						rows={3}
						maxLength={TIKTOK_ADS_MAX_AD_TEXT_LENGTH}
						value={draft.adText}
						onChange={(e) => updateDraft({ adText: e.target.value })}
						placeholder="Tell people what makes this worth a tap."
					/>
					<Form.Text>
						{draft.adText.length}/{TIKTOK_ADS_MAX_AD_TEXT_LENGTH} characters
					</Form.Text>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Label>Call to action</Form.Label>
					<Select
						value={draft.callToAction}
						onChange={(e) =>
							updateDraft({ callToAction: e.target.value as TikTokAdsCTA })
						}
					>
						{TIKTOK_ADS_CTA_OPTIONS.map((cta) => (
							<option key={cta} value={cta}>
								{cta.replace(/_/g, " ")}
							</option>
						))}
					</Select>
				</Form.Group>

				{isLeadGen ? (
					<Form.Group className="mb-3">
						<Form.Label>Instant Form</Form.Label>
						{formsLoading ? (
							<div>
								<Spinner size="sm" /> Loading Instant Forms…
							</div>
						) : (instantForms ?? []).length > 0 ? (
							<Select
								value={draft.instantFormPageId ?? ""}
								onChange={(e) =>
									updateDraft({
										instantFormPageId: e.target.value || null,
										landingPageUrl: "",
									})
								}
								className="mb-2"
							>
								<option value="">Select Instant Form…</option>
								{(instantForms ?? []).map((form) => (
									<option key={form.pageId} value={form.pageId}>
										{form.title}
										{form.status ? ` (${form.status})` : ""}
									</option>
								))}
							</Select>
						) : (
							<p className="text-muted mb-2">
								TikTok&apos;s API returned no Instant Forms yet. Forms still in{" "}
								<strong>Pending review</strong> often don&apos;t appear until
								they&apos;re published — paste the Page ID from Ads Manager
								below to continue.
							</p>
						)}
						<Form.Label className="mt-1">Page ID</Form.Label>
						<Form.Control
							value={draft.instantFormPageId ?? ""}
							onChange={(e) =>
								updateDraft({
									instantFormPageId: e.target.value.trim() || null,
									landingPageUrl: "",
								})
							}
							placeholder="e.g. 7681235238407913749"
						/>
						<Form.Text>
							Destination is the TikTok Instant Form (<code>page_id</code>), not
							a website URL. Copy Page ID from Ads Manager → Instant forms.
						</Form.Text>
					</Form.Group>
				) : (
					<>
						{isConversions && (
							<>
								<Form.Group className="mb-3">
									<Form.Label>TikTok Pixel</Form.Label>
									{pixelsLoading ? (
										<div>
											<Spinner size="sm" /> Loading Pixels…
										</div>
									) : (pixels ?? []).length > 0 ? (
										<Select
											value={draft.pixelId ?? ""}
											onChange={(e) =>
												updateDraft({
													pixelId: e.target.value || null,
												})
											}
										>
											<option value="">Select Pixel…</option>
											{(pixels ?? []).map((pixel) => (
												<option key={pixel.pixelId} value={pixel.pixelId}>
													{pixel.name}
													{pixel.status ? ` (${pixel.status})` : ""}
												</option>
											))}
										</Select>
									) : (
										<>
											<p className="text-muted mb-2">
												No Pixels found for this advertiser. Create one in
												TikTok Ads Manager → Assets → Events, or paste a Pixel
												ID below.
											</p>
											<Form.Control
												value={draft.pixelId ?? ""}
												onChange={(e) =>
													updateDraft({
														pixelId: e.target.value.trim() || null,
													})
												}
												placeholder="Pixel ID"
											/>
										</>
									)}
								</Form.Group>
								<Form.Group className="mb-3">
									<Form.Label>Optimization event</Form.Label>
									<Select
										value={draft.optimizationEvent ?? ""}
										onChange={(e) =>
											updateDraft({
												optimizationEvent: e.target.value || null,
											})
										}
									>
										<option value="">Select event…</option>
										{TIKTOK_ADS_OPTIMIZATION_EVENTS.map((event) => (
											<option key={event} value={event}>
												{event.replace(/_/g, " ")}
											</option>
										))}
									</Select>
									<Form.Text>
										Must match an event your Pixel fires on the landing page.
									</Form.Text>
								</Form.Group>
							</>
						)}
						<Form.Group className="mb-3">
							<Form.Label>Landing page URL</Form.Label>
							<Form.Control
								type="url"
								value={draft.landingPageUrl}
								onChange={(e) => applyUrl(e.target.value)}
								placeholder="https://example.com/offer"
							/>
						</Form.Group>
					</>
				)}

				<Form.Group className="mb-3">
					<Form.Label>Display name</Form.Label>
					<Form.Control
						value={draft.displayName}
						onChange={(e) => updateDraft({ displayName: e.target.value })}
						placeholder="example.com"
					/>
					<Form.Text>Shown next to the call-to-action button.</Form.Text>
				</Form.Group>

				{error && <p className="text-danger">{error}</p>}

				<div style={{ display: "flex", gap: 8 }}>
					<Button variant="secondary" onClick={prevStep}>
						Back
					</Button>
					<Button onClick={handleNext}>Continue</Button>
				</div>
			</Card.Body>
		</Card>
	);
};
